// Inbox Archive (Phase 1C) — Vuex store for the read-only Archive viewer.
// Lives separate from the main `inboxes` module so the active-inbox UX
// (chat list, dropdowns, dispatchers) stays untouched.
import ArchivesAPI from '../../api/archives';

const state = {
  archivedInboxes: [],
  currentInbox: null,
  conversations: [],
  conversationsMeta: { total: 0, page: 1, per_page: 25, pages: 0 },
  currentConversation: null,
  messages: [],
  messagesMeta: { total: 0, page: 1, per_page: 50, pages: 0 },
  uiFlags: {
    isFetchingInboxes: false,
    isFetchingInbox: false,
    isFetchingConversations: false,
    isFetchingMessages: false,
  },
};

const getters = {
  getArchivedInboxes: $state => $state.archivedInboxes,
  getArchivedInboxById: $state => id =>
    $state.archivedInboxes.find(item => Number(item.id) === Number(id)),
  getCurrentArchivedInbox: $state => $state.currentInbox,
  getArchivedConversations: $state => $state.conversations,
  getArchivedConversationsMeta: $state => $state.conversationsMeta,
  getCurrentArchivedConversation: $state => $state.currentConversation,
  getArchivedMessages: $state => $state.messages,
  getArchivedMessagesMeta: $state => $state.messagesMeta,
  getArchivesUIFlags: $state => $state.uiFlags,
};

const mutations = {
  SET_FLAG: ($state, { key, value }) => {
    $state.uiFlags = { ...$state.uiFlags, [key]: value };
  },
  SET_ARCHIVED_INBOXES: ($state, items) => {
    $state.archivedInboxes = items;
  },
  SET_CURRENT_ARCHIVED_INBOX: ($state, inbox) => {
    $state.currentInbox = inbox;
  },
  SET_ARCHIVED_CONVERSATIONS: ($state, { items, meta }) => {
    $state.conversations = items;
    $state.conversationsMeta = meta;
  },
  SET_CURRENT_ARCHIVED_CONVERSATION: ($state, conversation) => {
    $state.currentConversation = conversation;
  },
  SET_ARCHIVED_MESSAGES: ($state, { items, meta, conversation }) => {
    $state.messages = items;
    $state.messagesMeta = meta;
    if (conversation) $state.currentConversation = conversation;
  },
};

const actions = {
  async fetchInboxes({ commit }) {
    commit('SET_FLAG', { key: 'isFetchingInboxes', value: true });
    try {
      const { data } = await ArchivesAPI.get();
      commit('SET_ARCHIVED_INBOXES', data?.payload || []);
    } finally {
      commit('SET_FLAG', { key: 'isFetchingInboxes', value: false });
    }
  },
  async fetchInbox({ commit }, inboxId) {
    commit('SET_FLAG', { key: 'isFetchingInbox', value: true });
    try {
      const { data } = await ArchivesAPI.show(inboxId);
      commit('SET_CURRENT_ARCHIVED_INBOX', data);
      return data;
    } finally {
      commit('SET_FLAG', { key: 'isFetchingInbox', value: false });
    }
  },
  async fetchConversations(
    { commit },
    { inboxId, page = 1, perPage = 25, q = '' } = {}
  ) {
    commit('SET_FLAG', { key: 'isFetchingConversations', value: true });
    try {
      const { data } = await ArchivesAPI.conversations(inboxId, {
        page,
        perPage,
        q,
      });
      commit('SET_ARCHIVED_CONVERSATIONS', {
        items: data?.payload || [],
        meta: data?.meta || { total: 0, page, per_page: perPage, pages: 0 },
      });
    } finally {
      commit('SET_FLAG', { key: 'isFetchingConversations', value: false });
    }
  },
  async fetchMessages(
    { commit },
    { inboxId, conversationId, page = 1, perPage = 50 } = {}
  ) {
    commit('SET_FLAG', { key: 'isFetchingMessages', value: true });
    try {
      const { data } = await ArchivesAPI.messages(inboxId, conversationId, {
        page,
        perPage,
      });
      commit('SET_ARCHIVED_MESSAGES', {
        items: data?.payload || [],
        meta: data?.meta || { total: 0, page, per_page: perPage, pages: 0 },
        conversation: data?.conversation || null,
      });
    } finally {
      commit('SET_FLAG', { key: 'isFetchingMessages', value: false });
    }
  },
  clearCurrent({ commit }) {
    commit('SET_CURRENT_ARCHIVED_INBOX', null);
    commit('SET_ARCHIVED_CONVERSATIONS', {
      items: [],
      meta: { total: 0, page: 1, per_page: 25, pages: 0 },
    });
    commit('SET_CURRENT_ARCHIVED_CONVERSATION', null);
    commit('SET_ARCHIVED_MESSAGES', {
      items: [],
      meta: { total: 0, page: 1, per_page: 50, pages: 0 },
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
